import React, { useMemo, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, TrendingUp, ChevronDown, ChevronRight } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { formatCurrency } from '../../utils/formatters';

export interface CommissionInvoice {
  id: string;
  number: string;
  total: number;
  sale_type: string;
  client_name: string;
  date: string;
}

interface EmployeeSummary {
  id: string;
  name: string;
  role: string;
  base_salary: number;
  commission_percent: number;
}

interface CommissionDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  employee: EmployeeSummary | null;
  year: number;
  loading: boolean;
  invoices: CommissionInvoice[];
  commissionPercent: number;
}

const SALE_TYPE_LABELS: Record<string, string> = {
  production: 'Producción',
  express: 'Pedido Express',
  sample: 'Muestra',
  express_sample: 'Muestra Express',
};

const CommissionDetailModal: React.FC<CommissionDetailModalProps> = ({
  isOpen,
  onClose,
  employee,
  year,
  loading,
  invoices,
  commissionPercent,
}) => {
  const [expandedMonth, setExpandedMonth] = useState<number | null>(null);

  const monthly = useMemo(() => {
    const months = Array.from({ length: 12 }, (_, i) => ({
      month: i + 1,
      label: format(new Date(year, i, 1), 'MMMM', { locale: es }),
      invoiced: 0,
      commission: 0,
      invoices: [] as CommissionInvoice[],
    }));

    invoices.forEach((inv) => {
      if (!inv.date) return;
      const d = parseISO(inv.date);
      if (d.getFullYear() !== year) return;
      const m = d.getMonth();
      const amount = Number(inv.total) || 0;
      months[m].invoiced += amount;
      months[m].commission += amount * (commissionPercent / 100);
      months[m].invoices.push(inv);
    });

    months.forEach((m) => {
      m.invoices.sort((a, b) => (a.date || '').localeCompare(b.date || ''));
    });

    return months;
  }, [invoices, year, commissionPercent]);

  const totals = useMemo(
    () =>
      monthly.reduce(
        (acc, m) => ({
          invoiced: acc.invoiced + m.invoiced,
          commission: acc.commission + m.commission,
        }),
        { invoiced: 0, commission: 0 }
      ),
    [monthly]
  );

  const base = employee ? Number(employee.base_salary) || 0 : 0;
  const annualBase = base * 12;

  if (!employee) return null;

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[100] bg-black/50" />
        <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[100] w-full max-w-2xl max-h-[90vh] rounded-xl bg-white dark:bg-gray-800 shadow-xl overflow-hidden flex flex-col">
          <div className="px-6 pt-6 pb-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-indigo-500" />
                  Detalle de ventas — {employee.name}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {year} · Base mensual {formatCurrency(base)}
                  {employee.role === 'Vendedor' && commissionPercent > 0
                    ? ` · Comisión ${commissionPercent}%`
                    : ''}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="p-2 -m-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-3">
              <div className="rounded-lg bg-gray-50 dark:bg-gray-900/50 p-3">
                <p className="text-xs text-gray-500 dark:text-gray-400">Base anual (×12)</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white tabular-nums">
                  {formatCurrency(annualBase)}
                </p>
              </div>
              <div className="rounded-lg bg-gray-50 dark:bg-gray-900/50 p-3">
                <p className="text-xs text-gray-500 dark:text-gray-400">Facturado {year}</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white tabular-nums">
                  {formatCurrency(totals.invoiced)}
                </p>
              </div>
              <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/20 p-3">
                <p className="text-xs text-emerald-700/80 dark:text-emerald-400/80">Comisión {year}</p>
                <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-400 tabular-nums">
                  {formatCurrency(totals.commission)}
                </p>
              </div>
            </div>
          </div>

          <div className="overflow-y-auto flex-1 px-6 py-4">
            {loading ? (
              <p className="text-sm text-gray-500 text-center py-8">Cargando ventas…</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-2 px-2 font-medium text-gray-600 dark:text-gray-400 w-8" />
                    <th className="text-left py-2 px-2 font-medium text-gray-600 dark:text-gray-400">Mes</th>
                    <th className="text-right py-2 px-2 font-medium text-gray-600 dark:text-gray-400">Facturado</th>
                    <th className="text-right py-2 px-2 font-medium text-gray-600 dark:text-gray-400">Comisión</th>
                  </tr>
                </thead>
                <tbody>
                  {monthly.map((m) => {
                    const hasSales = m.invoices.length > 0;
                    const isOpen = expandedMonth === m.month;
                    return (
                      <React.Fragment key={m.month}>
                        <tr
                          className={`border-b border-gray-100 dark:border-gray-700/80 ${
                            hasSales
                              ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/40'
                              : ''
                          }`}
                          onClick={() => hasSales && setExpandedMonth(isOpen ? null : m.month)}
                        >
                          <td className="py-2 px-2 text-gray-400">
                            {hasSales ? (
                              isOpen ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : (
                                <ChevronRight className="h-4 w-4" />
                              )
                            ) : null}
                          </td>
                          <td className="py-2 px-2 capitalize text-gray-900 dark:text-white font-medium">
                            {m.label}
                            {hasSales && (
                              <span className="ml-2 text-xs font-normal text-gray-400">
                                {m.invoices.length} {m.invoices.length === 1 ? 'factura' : 'facturas'}
                              </span>
                            )}
                          </td>
                          <td className="py-2 px-2 text-right tabular-nums text-gray-900 dark:text-white">
                            {m.invoiced > 0 ? formatCurrency(m.invoiced) : (
                              <span className="text-gray-300 dark:text-gray-600">—</span>
                            )}
                          </td>
                          <td className="py-2 px-2 text-right tabular-nums font-medium text-emerald-600 dark:text-emerald-400">
                            {m.commission > 0 ? formatCurrency(m.commission) : (
                              <span className="text-gray-300 dark:text-gray-600">—</span>
                            )}
                          </td>
                        </tr>
                        {isOpen &&
                          m.invoices.map((inv) => (
                            <tr
                              key={inv.id}
                              className="bg-gray-50/80 dark:bg-gray-900/40 border-b border-gray-100 dark:border-gray-700/50"
                            >
                              <td className="py-1.5 px-2" />
                              <td className="py-1.5 px-2 pl-6 text-xs text-gray-600 dark:text-gray-400">
                                <span className="font-medium text-gray-800 dark:text-gray-200">
                                  {inv.client_name}
                                </span>
                                <span className="block text-gray-400">
                                  {inv.number || '—'} ·{' '}
                                  {inv.date
                                    ? format(parseISO(inv.date), 'd MMM', { locale: es })
                                    : '—'}{' '}
                                  · {SALE_TYPE_LABELS[inv.sale_type] || inv.sale_type}
                                </span>
                              </td>
                              <td className="py-1.5 px-2 text-right text-xs tabular-nums text-gray-700 dark:text-gray-300">
                                {formatCurrency(inv.total)}
                              </td>
                              <td className="py-1.5 px-2 text-right text-xs tabular-nums text-emerald-600 dark:text-emerald-400">
                                {formatCurrency((Number(inv.total) || 0) * (commissionPercent / 100))}
                              </td>
                            </tr>
                          ))}
                      </React.Fragment>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-indigo-200 dark:border-indigo-800 bg-indigo-50/60 dark:bg-indigo-900/20">
                    <td className="py-3 px-2" />
                    <td className="py-3 px-2 font-semibold text-indigo-800 dark:text-indigo-200">Total {year}</td>
                    <td className="py-3 px-2 text-right font-semibold tabular-nums text-indigo-900 dark:text-indigo-100">
                      {formatCurrency(totals.invoiced)}
                    </td>
                    <td className="py-3 px-2 text-right font-semibold tabular-nums text-emerald-700 dark:text-emerald-300">
                      {formatCurrency(totals.commission)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default CommissionDetailModal;
